import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import {
  Building2,
  User,
  MapPin,
  FileText,
  Image,
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Zap,
} from 'lucide-react';
import {
  useAdminCompanyDetail,
  useActivateCompany,
  useRejectCompany,
} from '../../../hooks/useCompanyRegistration';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Rascunho', color: 'bg-surface-600 text-surface-300' },
  DOCUMENTS_UPLOADED: { label: 'Documentos Enviados', color: 'bg-blue-500/20 text-blue-400' },
  PENDING_COMPLIANCE: { label: 'Em Análise', color: 'bg-yellow-500/20 text-yellow-400' },
  APPROVED: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  REJECTED: { label: 'Rejeitado', color: 'bg-red-500/20 text-red-400' },
  PENDING_ADDITIONAL_DOCS: { label: 'Docs Adicionais', color: 'bg-orange-500/20 text-orange-400' },
  ACTIVE: { label: 'Ativo', color: 'bg-emerald-500/20 text-emerald-400' },
};

const DOC_TYPE_LABELS: Record<string, string> = {
  SOCIAL_CONTRACT: 'Contrato Social',
  CNPJ_CARD: 'Cartão CNPJ',
  DOC_FRONT: 'Documento — Frente',
  DOC_BACK: 'Documento — Verso',
  SELFIE_WITH_DOC: 'Selfie com Documento',
  DOC_PDF: 'PDF do Documento',
  ADDITIONAL: 'Documento Adicional',
};

function formatCnpj(cnpj: string) {
  const d = cnpj?.replace(/\D/g, '') || '';
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function maskCpf(cpf: string) {
  const d = cpf?.replace(/\D/g, '') || '';
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: company, isLoading } = useAdminCompanyDetail(id || '');
  const activateMutation = useActivateCompany();
  const rejectMutation = useRejectCompany();

  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [gatewayCompanyId, setGatewayCompanyId] = useState('');
  const [activateNotes, setActivateNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-400" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-20 text-surface-400">Empresa não encontrada.</div>
    );
  }

  const statusInfo = STATUS_LABELS[company.status] || { label: company.status, color: 'bg-surface-700 text-surface-300' };
  const canActivate = company.status === 'APPROVED';
  const canReject = ['PENDING_COMPLIANCE', 'DOCUMENTS_UPLOADED', 'APPROVED'].includes(company.status);

  const handleActivate = () => {
    if (!gatewayCompanyId) return;
    activateMutation.mutate({ id: company.id, gatewayCompanyId, notes: activateNotes }, {
      onSuccess: () => setShowActivateModal(false),
    });
  };

  const handleReject = () => {
    if (rejectionReason.length < 10) return;
    rejectMutation.mutate({ id: company.id, rejectionReason }, {
      onSuccess: () => setShowRejectModal(false),
    });
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/companies')} className="text-surface-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{company.companyName}</h1>
            <p className="text-surface-400 font-mono text-sm">{formatCnpj(company.companyCnpj)}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
          {statusInfo.label}
        </span>
      </div>

      {/* Action Buttons */}
      {(canActivate || canReject) && (
        <div className="flex gap-3">
          {canActivate && (
            <Button variant="primary" onClick={() => setShowActivateModal(true)}>
              <CheckCircle size={16} className="mr-2" /> Ativar Empresa
            </Button>
          )}
          {canReject && (
            <Button variant="secondary" onClick={() => setShowRejectModal(true)}>
              <XCircle size={16} className="mr-2" /> Rejeitar
            </Button>
          )}
        </div>
      )}

      {/* Company Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 size={18} className="text-primary-400" /> Dados da Empresa
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="Nome" value={company.companyName} />
              <Row label="CNPJ" value={formatCnpj(company.companyCnpj)} />
              <Row label="Tipo" value={company.companyType} />
              <Row label="Nome Fantasia" value={company.tradeName} />
              <Row label="Email" value={company.companyEmail} />
              <Row label="Telefone" value={company.companyPhone} />
              {company.companyWebsite && <Row label="Website" value={company.companyWebsite} />}
              <Row label="Descrição" value={company.companyDescription} />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <User size={18} className="text-primary-400" /> Representante Legal
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="Nome" value={company.representativeName} />
              <Row label="CPF" value={maskCpf(company.representativeCpf)} />
              <Row label="Documento" value={company.representativeDocType} />
              <Row label="Nascimento" value={company.representativeBirthDate} />
              <Row label="Telefone" value={company.representativePhone} />
              <Row label="Nome da Mãe" value={company.representativeMotherName} />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <MapPin size={18} className="text-primary-400" /> Endereço
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="CEP" value={company.addressZipCode} />
              <Row label="Logradouro" value={`${company.addressStreet}, ${company.addressNumber}`} />
              <Row label="Bairro" value={company.addressNeighborhood} />
              <Row label="Cidade/UF" value={`${company.addressCity} - ${company.addressState}`} />
              {company.addressComplement && <Row label="Complemento" value={company.addressComplement} />}
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock size={18} className="text-primary-400" /> Informações
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="Email da Conta" value={company.accountEmail} />
              <Row label="Cadastrado em" value={formatDate(company.createdAt)} />
              {company.submittedAt && <Row label="Enviado em" value={formatDate(company.submittedAt)} />}
              {company.approvedAt && <Row label="Aprovado em" value={formatDate(company.approvedAt)} />}
              {company.rejectedAt && <Row label="Rejeitado em" value={formatDate(company.rejectedAt)} />}
              {company.rejectionReason && <Row label="Motivo Rejeição" value={company.rejectionReason} />}
              {company.gatewayCompanyId && <Row label="ID Gateway" value={company.gatewayCompanyId} />}
              <Row label="Reenvios" value={String(company.resubmissionCount)} />
            </div>
          </div>
        </Card>
      </div>

      {/* Documents */}
      {company.documents && company.documents.length > 0 && (
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText size={18} className="text-primary-400" /> Documentos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {company.documents.filter(d => !d.isReplaced).map(doc => (
                <div key={doc.id} className="p-3 rounded-lg bg-surface-800 space-y-2">
                  <div className="flex items-center gap-2">
                    {doc.fileMimeType?.startsWith('image/') ? (
                      <Image size={16} className="text-blue-400" />
                    ) : (
                      <FileText size={16} className="text-red-400" />
                    )}
                    <span className="text-sm text-white font-medium">
                      {DOC_TYPE_LABELS[doc.documentType] || doc.documentType}
                    </span>
                  </div>
                  <p className="text-xs text-surface-400 truncate">{doc.fileName}</p>
                  <p className="text-xs text-surface-500">{(doc.fileSizeBytes / 1024).toFixed(0)} KB</p>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Timeline */}
      {company.history && company.history.length > 0 && (
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">Histórico</h2>
            <div className="space-y-3">
              {company.history.map(entry => (
                <div key={entry.id} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-surface-300">
                      {entry.action.replace(/_/g, ' ')}
                      {entry.toStatus && (
                        <span className="text-surface-500"> → {STATUS_LABELS[entry.toStatus]?.label || entry.toStatus}</span>
                      )}
                    </p>
                    <p className="text-xs text-surface-500">{formatDate(entry.changedAt)}</p>
                    {entry.notes && <p className="text-xs text-surface-400 mt-0.5">{entry.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Activate Modal */}
      <Modal isOpen={showActivateModal} onClose={() => setShowActivateModal(false)} title="Ativar Empresa" size="md">
        <div className="space-y-4">
          <p className="text-surface-300 text-sm">
            Confirme a ativação de <strong className="text-white">{company.companyName}</strong> ({formatCnpj(company.companyCnpj)}).
          </p>
          <Input label="ID da Empresa no Gateway *" placeholder="ID retornado pelo gateway"
            value={gatewayCompanyId} onChange={e => setGatewayCompanyId(e.target.value)} />
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">Observações</label>
            <textarea className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white min-h-[60px]"
              placeholder="Observações opcionais..." value={activateNotes} onChange={e => setActivateNotes(e.target.value)} />
          </div>
          {activateMutation.isError && (
            <p className="text-red-400 text-sm flex items-center gap-1">
              <AlertCircle size={14} /> {(activateMutation.error as any)?.response?.data?.message || 'Erro ao ativar.'}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowActivateModal(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleActivate} disabled={!gatewayCompanyId || activateMutation.isPending}>
              {activateMutation.isPending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
              Ativar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Rejeitar Empresa" size="md">
        <div className="space-y-4">
          <p className="text-surface-300 text-sm">
            Informe o motivo da rejeição de <strong className="text-white">{company.companyName}</strong>.
          </p>
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">Motivo da Rejeição *</label>
            <textarea className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-white min-h-[80px]"
              placeholder="Mínimo 10 caracteres..." value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} />
            {rejectionReason.length > 0 && rejectionReason.length < 10 && (
              <p className="text-red-400 text-xs mt-1">Mínimo 10 caracteres ({rejectionReason.length}/10)</p>
            )}
          </div>
          {rejectMutation.isError && (
            <p className="text-red-400 text-sm flex items-center gap-1">
              <AlertCircle size={14} /> {(rejectMutation.error as any)?.response?.data?.message || 'Erro ao rejeitar.'}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleReject} disabled={rejectionReason.length < 10 || rejectMutation.isPending}>
              {rejectMutation.isPending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <XCircle size={16} className="mr-2" />}
              Rejeitar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-surface-400">{label}</span>
      <span className="text-white text-right max-w-[60%] break-words">{value || '—'}</span>
    </div>
  );
}
